import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TaskCardComponent } from './task-card.component';
import { ProjectTask } from '../../../models/task-management.models';
import { CdkDragDrop, moveItemInArray, transferArrayItem, DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-task-list',
  standalone: true,
  imports: [
    CommonModule,
    TaskCardComponent,
    DragDropModule
  ],
  template: `
    <div class="task-list-container">
      <!-- Empty State -->
      <div class="empty-state" *ngIf="!tasks || tasks.length === 0">
        <div class="empty-content">
          <i class="pi pi-inbox empty-icon"></i>
          <h4>{{ emptyStateTitle || 'No tasks found' }}</h4>
          <p>{{ emptyStateMessage || 'Try adjusting your filters or create a new task.' }}</p>
          <button 
            *ngIf="showCreateButton"
            class="p-button p-button-outlined"
            (click)="onCreateTask()">
            <i class="pi pi-plus"></i>
            Create Task
          </button>
        </div>
      </div>

      <!-- Task List -->
      <div 
        class="task-list" 
        *ngIf="tasks && tasks.length > 0"
        [attr.cdkDropListData]="tasks"
        cdkDropList
        [cdkDropListConnectedTo]="connectedDropLists"
        (cdkDropListDropped)="onTaskDrop($event)">
        
        <app-task-card
          *ngFor="let task of tasks; trackBy: trackByTaskId"
          [task]="task"
          [showDescription]="showDescription"
          [showStatus]="showStatus"
          [showTags]="showTags"
          [draggable]="draggable"
          cdkDrag
          [cdkDragData]="task"
          (taskClick)="onTaskClick(task)"
          (taskEdit)="onTaskEdit(task)"
          (taskDelete)="onTaskDelete(task)"
          (statusChange)="onStatusChange($event)" />
      </div>

      <!-- Load More -->
      <div class="load-more-container" *ngIf="hasMoreTasks">
        <button 
          class="p-button p-button-text"
          (click)="onLoadMore()"
          [disabled]="loading">
          <i class="pi pi-spinner pi-spin" *ngIf="loading"></i>
          <i class="pi pi-chevron-down" *ngIf="!loading"></i>
          {{ loading ? 'Loading...' : 'Load More' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .task-list-container {
      width: 100%;
    }

    .empty-state {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 300px;
      padding: 32px;
    }

    .empty-content {
      text-align: center;
      max-width: 400px;
    }

    .empty-icon {
      font-size: 48px;
      color: #bdc3c7;
      margin-bottom: 16px;
    }

    .empty-content h4 {
      color: #7f8c8d;
      margin-bottom: 8px;
      font-weight: 500;
    }

    .empty-content p {
      color: #95a5a6;
      margin-bottom: 24px;
      line-height: 1.5;
    }

    .task-list {
      min-height: 100px;
    }

    .task-list.cdk-drop-list-dragging .task-card:not(.cdk-drag-placeholder) {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }

    .cdk-drag-preview {
      box-sizing: border-box;
      border-radius: 8px;
      box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
                  0 8px 10px 1px rgba(0, 0, 0, 0.14),
                  0 3px 14px 2px rgba(0, 0, 0, 0.12);
    }

    .cdk-drag-placeholder {
      opacity: 0;
    }

    .cdk-drag-animating {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }

    .load-more-container {
      text-align: center;
      padding: 16px;
    }

    @media (max-width: 768px) {
      .empty-state {
        min-height: 200px;
        padding: 16px;
      }

      .empty-icon {
        font-size: 36px;
      }

      .empty-content {
        max-width: 300px;
      }
    }
  `]
})
export class TaskListComponent {
  @Input() tasks: ProjectTask[] = [];
  @Input() showDescription: boolean = true;
  @Input() showStatus: boolean = false;
  @Input() showTags: boolean = true;
  @Input() draggable: boolean = false;
  @Input() connectedDropLists: string[] = [];
  @Input() emptyStateTitle?: string;
  @Input() emptyStateMessage?: string;
  @Input() showCreateButton: boolean = true;
  @Input() hasMoreTasks: boolean = false;
  @Input() loading: boolean = false;

  @Output() taskClick = new EventEmitter<ProjectTask>();
  @Output() taskEdit = new EventEmitter<ProjectTask>();
  @Output() taskDelete = new EventEmitter<ProjectTask>();
  @Output() statusChange = new EventEmitter<{task: ProjectTask, statusId: number}>();
  @Output() taskDrop = new EventEmitter<{task: ProjectTask, fromIndex: number, toIndex: number, fromList?: string, toList?: string}>();
  @Output() createTask = new EventEmitter<void>();
  @Output() loadMore = new EventEmitter<void>();

  trackByTaskId(index: number, task: ProjectTask): number {
    return task.taskId;
  }

  onTaskClick(task: ProjectTask) {
    this.taskClick.emit(task);
  }

  onTaskEdit(task: ProjectTask) {
    this.taskEdit.emit(task);
  }

  onTaskDelete(task: ProjectTask) {
    this.taskDelete.emit(task);
  }

  onStatusChange(event: {task: ProjectTask, statusId: number}) {
    this.statusChange.emit(event);
  }

  onTaskDrop(event: CdkDragDrop<ProjectTask[]>) {
    if (event.previousContainer === event.container) {
      // Reordering within the same list
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // Moving between lists
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Emit the drop event with additional metadata
    const task = event.item.data as ProjectTask;
    this.taskDrop.emit({
      task: task,
      fromIndex: event.previousIndex,
      toIndex: event.currentIndex,
      fromList: event.previousContainer.id,
      toList: event.container.id
    });
  }

  onCreateTask() {
    this.createTask.emit();
  }

  onLoadMore() {
    if (!this.loading) {
      this.loadMore.emit();
    }
  }
}